import React, { useState, useEffect } from 'react';
import { getRollupSummary, rollupSOVLine, getProject } from '../api';
import { useTheme } from '../hooks/useTheme';

const formatCurrency = (value) => {
  return `$${(value || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatCurrencyFull = (value) => {
  return `$${(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const RollupTab = ({ projectId, refreshSummary }) => {
  const t = useTheme();
  const [rollupData, setRollupData] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [data, proj] = await Promise.all([
        getRollupSummary(projectId),
        getProject(projectId),
      ]);
      setRollupData(data);
      setProject(proj);
    } catch (err) {
      console.error('Error fetching rollup data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateRollup = async () => {
    try {
      setCalculating(true);
      // Rollup each SOV line
      if (rollupData?.lines) {
        for (const line of rollupData.lines) {
          await rollupSOVLine(line.id);
        }
      }
      await fetchData();
      if (refreshSummary) refreshSummary();
    } catch (err) {
      console.error('Error calculating rollup:', err);
    } finally {
      setCalculating(false);
    }
  };

  if (loading && !rollupData) {
    return <div style={{ padding: '40px', textAlign: 'center', color: t.textSecondary }}>Loading rollup data...</div>;
  }

  if (!rollupData) {
    return <div style={{ padding: '40px', textAlign: 'center', color: t.textSecondary }}>No data available</div>;
  }

  const { summary, lines } = rollupData;
  const projectContract = project?.contract_amount || summary.total_contract;
  const sovPctOfProject = projectContract > 0 ? (summary.total_contract / projectContract) * 100 : 0;
  const allocPctOfSov = summary.total_contract > 0 ? (summary.total_allocated / summary.total_contract) * 100 : 0;

  // Summary card style
  const cardStyle = {
    flex: 1,
    minWidth: '200px',
    padding: '16px 20px',
    borderRadius: '12px',
    border: `1px solid ${t.cardBorder}`,
    backgroundColor: t.bg,
  };
  const cardLabelStyle = {
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: t.textSecondary,
    marginBottom: '6px',
  };
  const cardValueStyle = {
    fontSize: '24px',
    fontWeight: 700,
    color: t.heading,
  };
  const cardSubStyle = {
    fontSize: '12px',
    color: t.textSecondary,
    marginTop: '2px',
  };

  return (
    <div>
      {/* Summary Cards */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <div style={cardStyle}>
          <div style={cardLabelStyle}>Project Contract</div>
          <div style={cardValueStyle}>{formatCurrency(projectContract)}</div>
        </div>
        <div style={cardStyle}>
          <div style={cardLabelStyle}>Total SOV Contract</div>
          <div style={cardValueStyle}>{formatCurrency(summary.total_contract)}</div>
          <div style={cardSubStyle}>{sovPctOfProject.toFixed(1)}% of project contract</div>
        </div>
        <div style={{
          ...cardStyle,
          borderColor: allocPctOfSov >= 99.9 ? '#86efac' : '#fde68a',
          backgroundColor: allocPctOfSov >= 99.9 ? '#f0fdf4' : '#fffbeb',
        }}>
          <div style={cardLabelStyle}>Total Task-Allocated</div>
          <div style={{ ...cardValueStyle, color: allocPctOfSov >= 99.9 ? '#16a34a' : '#d97706' }}>
            {formatCurrency(summary.total_allocated)}
          </div>
          <div style={cardSubStyle}>{allocPctOfSov.toFixed(1)}% of SOV allocated</div>
        </div>
        <div style={cardStyle}>
          <div style={cardLabelStyle}>Unallocated SOV</div>
          <div style={cardValueStyle}>{formatCurrency(summary.unallocated)}</div>
          <div style={cardSubStyle}>remaining to link to tasks</div>
        </div>
      </div>

      {/* Header row */}
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: t.heading }}>
          SOV Rollup from Tasks
        </h3>
        <p style={{ margin: '4px 0 0', fontSize: '13px', color: t.textSecondary }}>
          Rollup updates automatically when task progress or SOV allocations change.
        </p>
      </div>

      {/* Table */}
      <div style={{
        border: `1px solid ${t.cardBorder}`, borderRadius: '12px', overflow: 'hidden',
        backgroundColor: t.bg,
      }}>
        {/* Table Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 0.8fr 1.2fr 1.5fr 1fr',
          padding: '14px 20px',
          backgroundColor: t.bgAlt,
          borderBottom: `2px solid ${t.tableBorder}`,
          fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.5px', color: t.textSecondary,
        }}>
          <div>SOV Line</div>
          <div style={{ textAlign: 'right' }}>Contract</div>
          <div style={{ textAlign: 'center' }}>Tasks Linked</div>
          <div style={{ textAlign: 'right' }}>Allocated</div>
          <div style={{ textAlign: 'center' }}>Calc % Complete</div>
          <div style={{ textAlign: 'right' }}>Calc Billed</div>
        </div>

        {/* Table Rows */}
        {lines.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: t.textSecondary, fontSize: '14px' }}>
            No SOV lines found. Add SOV lines in the SOV tab first.
          </div>
        ) : (
          lines.map((line, idx) => {
            const pctComplete = line.calc_percent_complete || 0;
            const barColor = pctComplete >= 100 ? '#22c55e' : pctComplete >= 50 ? '#16a34a' : pctComplete > 0 ? '#3b82f6' : '#e2e8f0';

            return (
              <div key={line.id} style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 0.8fr 1.2fr 1.5fr 1fr',
                padding: '16px 20px',
                borderBottom: idx < lines.length - 1 ? `1px solid ${t.tableBorder}` : 'none',
                alignItems: 'center',
                transition: 'background-color 0.1s',
              }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = t.bgAlt}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                {/* SOV Line */}
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: t.heading }}>
                    {line.description}
                  </div>
                  <div style={{ fontSize: '12px', color: t.textSecondary, marginTop: '2px' }}>
                    {line.sov_code}
                  </div>
                </div>

                {/* Contract */}
                <div style={{ textAlign: 'right', fontSize: '14px', fontWeight: 500, color: t.text }}>
                  {formatCurrency(line.contract_amount)}
                </div>

                {/* Tasks Linked */}
                <div style={{ textAlign: 'center' }}>
                  {line.tasks_linked > 0 ? (
                    <span style={{
                      display: 'inline-block', minWidth: '28px',
                      padding: '2px 10px', borderRadius: '12px',
                      fontSize: '13px', fontWeight: 600,
                      color: '#0d9488', backgroundColor: '#f0fdfa',
                      border: '1px solid #99f6e4',
                    }}>
                      {line.tasks_linked}
                    </span>
                  ) : (
                    <span style={{ color: '#cbd5e1', fontSize: '16px' }}>—</span>
                  )}
                </div>

                {/* Allocated */}
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    fontSize: '14px', fontWeight: 500,
                    color: line.allocated_pct >= 99.9 ? '#16a34a' : '#d97706',
                  }}>
                    {formatCurrency(line.total_allocated)}
                  </div>
                  <div style={{ fontSize: '11px', color: t.textSecondary, marginTop: '1px' }}>
                    {line.allocated_pct.toFixed(1)}%
                  </div>
                </div>

                {/* Calc % Complete */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
                  <div style={{
                    flex: 1, maxWidth: '120px', height: '8px',
                    backgroundColor: t.bgAlt, borderRadius: '4px', overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${Math.min(pctComplete, 100)}%`, height: '100%',
                      backgroundColor: barColor, borderRadius: '4px',
                      transition: 'width 0.3s ease',
                    }} />
                  </div>
                  <span style={{ fontSize: '13px', fontWeight: 500, color: t.text, minWidth: '42px', textAlign: 'right' }}>
                    {pctComplete.toFixed(1)}%
                  </span>
                </div>

                {/* Calc Billed */}
                <div style={{ textAlign: 'right', fontSize: '14px', fontWeight: 500, color: t.text }}>
                  {formatCurrency(line.calc_billed)}
                </div>
              </div>
            );
          })
        )}

        {/* Totals Row */}
        {lines.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 0.8fr 1.2fr 1.5fr 1fr',
            padding: '14px 20px',
            borderTop: `2px solid ${t.tableBorder}`,
            backgroundColor: t.bgAlt,
            fontSize: '14px', fontWeight: 700, color: t.heading,
          }}>
            <div>Totals</div>
            <div style={{ textAlign: 'right' }}>{formatCurrency(summary.total_contract)}</div>
            <div style={{ textAlign: 'center' }}>{lines.reduce((sum, l) => sum + l.tasks_linked, 0)}</div>
            <div style={{ textAlign: 'right' }}>{formatCurrency(summary.total_allocated)}</div>
            <div style={{ textAlign: 'center' }}>
              {(() => {
                const totalAlloc = summary.total_allocated || 0;
                const weightedComplete = totalAlloc > 0
                  ? lines.reduce((sum, l) => sum + (l.total_allocated * l.calc_percent_complete / 100), 0) / totalAlloc * 100
                  : 0;
                return `${weightedComplete.toFixed(1)}%`;
              })()}
            </div>
            <div style={{ textAlign: 'right' }}>{formatCurrency(summary.total_calc_billed)}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RollupTab;
